import { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Plus, FileText } from 'lucide-react';
import { SmartSearchInput } from '../components/SmartSearchInput';
import {
  buildSearchSuggestions,
  filterBySmartSearch,
  normalizeSearchText,
} from '../lib/smartSearch';
import { useAuth } from '../contexts/AuthContext';
import { useStatutes, type Statute } from '../hooks/useStatutes';
import UploadStatuteModal from '../components/UploadStatuteModal';
import { StatuteAntiScrapeBoundary } from '../components/StatuteAntiScrapeBoundary';
import { StatuteGridCard } from '../components/library/StatuteGridCard';
import { statuteCardFooterMeta, statuteCardFullTitle } from '../lib/statuteLandingMeta';
import CaseCategoryFilter from '../components/CaseCategoryFilter';
import { AsyncContentReveal } from '../components/motion/AsyncContentReveal';
import { FirestoreLoadingSpinner } from '../components/motion/FirestoreLoadingSpinner';
import { RevealOnMount } from '../components/motion/RevealOnMount';
import type { TopFilter } from '../lib/caseTaxonomyFilter';
import {
  classifyStatute,
  matchesStatuteTaxonomy,
  STATUTE_OTHER_SUBFILTER_OPTIONS,
  STATUTE_SUBFILTER_OPTIONS,
  sortStatutesForLibrary,
  statuteCategorySortKey,
  statuteTopFilterToDefaultSubFilter,
  topFilterSectionLabel,
  type StatuteClassification,
} from '../lib/statuteTaxonomyFilter';
import {
  pickFeaturedStatutes,
  STATUTES_INDEX_SEO,
} from '../lib/statuteSeoCopy';
import statutesSeo from '../data/statutes-seo.json';

type EnrichedStatute = Statute & {
  classification: StatuteClassification;
};

type LibraryLandingProps = {
  isPublic?: boolean;
  basePath?: string;
};

export default function LibraryLanding({ isPublic = false, basePath = '/library' }: LibraryLandingProps) {
  const { statutes, loading } = useStatutes();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [topFilter, setTopFilter] = useState<TopFilter>('All');
  const [subFilter, setSubFilter] = useState('All Civil Law');

  useEffect(() => {
    const q = searchParams.get('q')?.trim();
    if (q) setSearchQuery(q);
  }, [searchParams]);

  const featuredStatutes = useMemo(
    () =>
      pickFeaturedStatutes(
        statutesSeo as Record<string, { id: string; displayTitle: string; canonical: string }>,
      ),
    [],
  );

  const statutesEnriched = useMemo<EnrichedStatute[]>(() => {
    return statutes.map((s) => ({
      ...s,
      classification: classifyStatute(s),
    }));
  }, [statutes]);

  const getStatuteSearchFields = (s: EnrichedStatute) => [
    s.title,
    s.description,
    s.category,
    s.classification.practiceAreaLabel,
    s.classification.subjectLabel,
    s.classification.scopeLabel,
  ];

  const statuteSearchSuggestions = useMemo(
    () =>
      buildSearchSuggestions(statutesEnriched, searchQuery, {
        getFields: getStatuteSearchFields,
        getId: (s) => s.id,
        getLabel: (s) => statuteCardFullTitle(s),
        getSubtitle: (s) => s.classification.practiceAreaLabel,
        getValue: (s) => s.title || statuteCardFullTitle(s),
        limit: 8,
        minChars: 1,
      }),
    [statutesEnriched, searchQuery],
  );

  const searchFiltered = useMemo(() => {
    const q = normalizeSearchText(searchQuery);
    if (!q) return statutesEnriched;

    const ranked = filterBySmartSearch(statutesEnriched, q, getStatuteSearchFields);
    const rankedIds = new Set(ranked.map((s) => s.id));

    const aliasMatches = statutesEnriched.filter(
      (s) =>
        !rankedIds.has(s.id) &&
        ((q.includes('qso') && s.classification.crossCutting === 'evidence-qso') ||
          (q.includes('shahadat') && s.classification.crossCutting === 'evidence-qso')),
    );

    return [...ranked, ...aliasMatches];
  }, [statutesEnriched, searchQuery]);

  const taxonomyFiltered = useMemo(() => {
    const matched = searchFiltered.filter((s) =>
      matchesStatuteTaxonomy(s.classification, topFilter, subFilter),
    );
    return sortStatutesForLibrary(matched, topFilter);
  }, [searchFiltered, topFilter, subFilter]);

  const groupedEntries = useMemo(() => {
    const grouped: Partial<Record<TopFilter, EnrichedStatute[]>> = {};
    searchFiltered.forEach((s) => {
      const key = s.classification.topFilter;
      if (!grouped[key]) grouped[key] = [];
      grouped[key]!.push(s);
    });
    return (Object.entries(grouped) as [TopFilter, EnrichedStatute[]][])
      .filter(([, items]) => items.length > 0)
      .map(([category, items]) => [
        category,
        sortStatutesForLibrary(items, category),
      ] as [TopFilter, EnrichedStatute[]])
      .sort(([a], [b]) => statuteCategorySortKey(a) - statuteCategorySortKey(b));
  }, [searchFiltered]);

  const renderCard = (statute: EnrichedStatute) => {
    const { jurisdiction, category, sectionCount } = statuteCardFooterMeta(statute);
    const sectionMeta =
      sectionCount != null
        ? `${sectionCount} section${sectionCount === 1 ? '' : 's'}`
        : 'Open to view';
    return (
      <StatuteGridCard
        key={statute.id}
        metaLabel={jurisdiction}
        title={statuteCardFullTitle(statute)}
        footerLabel={sectionMeta}
        categoryPill={category}
        onClick={() => navigate(`${basePath}/${statute.id}`)}
      />
    );
  };

  const emptySearch = statutes.length > 0 && taxonomyFiltered.length === 0;
  const showGrouped = topFilter === 'All' && groupedEntries.length > 0;

  return (
    <StatuteAntiScrapeBoundary>
      <div className="app-page py-4 md:py-8 space-y-6">
        <RevealOnMount index={0}>
          <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
            <div className="max-w-3xl">
              {isPublic ? (
                <>
                  <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-text-main">
                    Pakistani Statutes &amp; Laws
                  </h1>
                  <p className="text-[#64748B] text-sm md:text-base mt-2 leading-relaxed">
                    {STATUTES_INDEX_SEO.description}
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-bold tracking-tight text-text-main">Legal Library</h1>
                  <p className="text-[#64748B] text-sm mt-1">Find and search your legal statutes</p>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {!isPublic && isAdmin && (
                <button
                  type="button"
                  onClick={() => setIsUploadOpen(true)}
                  className="btn-primary flex items-center gap-2 flex-shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  Add Statute
                </button>
              )}
            </div>
          </header>
        </RevealOnMount>

        {isPublic && featuredStatutes.length > 0 ? (
          <RevealOnMount index={1}>
            <nav aria-label="Popular statutes" className="flex flex-wrap gap-2">
              {featuredStatutes.map((statute) => (
                <Link
                  key={statute.id}
                  to={`${basePath}/${statute.id}`}
                  className="rounded-full border border-border-light bg-white px-4 py-2 text-sm font-medium text-text-main hover:border-primary/30 hover:bg-primary/5 transition-colors"
                >
                  {statute.displayTitle}
                </Link>
              ))}
            </nav>
          </RevealOnMount>
        ) : null}

        <RevealOnMount index={isPublic && featuredStatutes.length > 0 ? 2 : 1}>
          <SmartSearchInput
            className="w-full max-w-sm"
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search statutes by name or keyword..."
            suggestions={statuteSearchSuggestions}
            ariaLabel="Search statutes"
          />
        </RevealOnMount>

        <RevealOnMount index={isPublic && featuredStatutes.length > 0 ? 3 : 2} variant="fade">
          <CaseCategoryFilter
            pillScope="library"
            topFilter={topFilter}
            subFilter={subFilter}
            onTopFilterChange={(top) => {
              if (top !== 'Unlinked') setTopFilter(top);
            }}
            onSubFilterChange={setSubFilter}
            subFilterOptionsMap={STATUTE_SUBFILTER_OPTIONS}
            resolveDefaultSubFilter={statuteTopFilterToDefaultSubFilter}
            otherSubFilterOptions={STATUTE_OTHER_SUBFILTER_OPTIONS}
          />
        </RevealOnMount>

        <AsyncContentReveal loading={loading} minHeight="min-h-[420px]" skeleton={<FirestoreLoadingSpinner />}>
          {statutes.length === 0 ? (
          <div className="py-20 text-center border-2 border-dashed border-border-light rounded-2xl bg-white shadow-sm">
            <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-bold text-text-main mb-1">No statutes yet</h3>
            <p className="text-sm text-[#64748B] mb-6">
              {isAdmin ? 'Add your first statute to build the reference desk.' : 'Your workspace library is empty.'}
            </p>
            {!isPublic && isAdmin ? (
              <button
                type="button"
                onClick={() => setIsUploadOpen(true)}
                className="bg-white border border-border-light text-text-main px-6 py-2 rounded-xl font-bold text-sm tracking-wide hover:shadow-sm transition-all shadow-sm"
              >
                Add Statute
              </button>
            ) : null}
          </div>
        ) : emptySearch ? (
          <div className="py-20 text-center border-2 border-dashed border-border-light rounded-2xl bg-white shadow-sm">
            <h3 className="text-lg font-bold text-text-main mb-1">No statutes match your filters</h3>
            <p className="text-sm text-[#64748B] mb-4">Try a different search term or clear filters.</p>
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setTopFilter('All');
                setSubFilter('All Civil Law');
              }}
              className="text-sm font-bold text-primary hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : showGrouped ? (
          <div className="space-y-10">
            {groupedEntries.map(([category, items]) => (
              <section key={category}>
                <h2 className="text-xl font-bold text-text-main tracking-tight mb-4">
                  {topFilterSectionLabel(category)} ({items.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((s) => renderCard(s))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {taxonomyFiltered.map((s) => renderCard(s))}
          </div>
        )}
        </AsyncContentReveal>
      </div>

      <UploadStatuteModal isOpen={isUploadOpen} onClose={() => setIsUploadOpen(false)} />
    </StatuteAntiScrapeBoundary>
  );
}
